import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  IconButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import HubIcon from "@mui/icons-material/Hub";
import GroupIcon from "@mui/icons-material/Group";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import confetti from "canvas-confetti";
import ClusterDenLogo from "../../Images/Login/ClusterDenLogo.png";
import ClusterDenLogoWhite from "../../Images/Login/ClusterDenLogoWhite.png";
import WelcomeIllustration from "../../Images/Onboard/welcome-illustration.png";
import { setCurrentPage } from "../../redux/Slice/Dashboard/leftNavSlice";
import { setSelectedOption } from "../../redux/Slice/Settings/settingsSlice";
import { useThemeContext } from "../../contexts/ThemeContext";
import { getAccessToken } from "../../utils/tokenManager";
import { getAuthToken } from "../../utils/getAuthToken";
import { fetchOnboardStep, updateOnboardStep as pushOnboardStep } from "../../services/accountApi";

const WHATSAPP_URL = "https://wa.me/clusterden";

// Same short-viewport pattern as the Auth pages (Login/Signup/Setup use 800px;
// this page sits inside the main app shell like LeftNav's 900px rail, splitting
// the difference). Shrinks fixed chrome (padding/typography/images) so small-
// laptop windows need less (ideally no) scrolling to see everything.
const ONBOARD_COMPACT_HEIGHT_MQ = "@media (max-height:850px)";

const onboards = [
  {
    key: "channel",
    question: "How do I connect a channel?",
    stepTitle: "Connect a channel",
    answer: "Connect WhatsApp, Facebook, or Instagram to start receiving and sending messages from your inbox.",
    Icon: HubIcon,
    StepIcon: ShareRoundedIcon,
    stepColor: "#0EA5E9",
    settingsOption: "Integrations",
  },
  {
    key: "team",
    question: "How do I add a team member?",
    stepTitle: "Add your team",
    answer: "Invite teammates and assign them roles so they can help manage conversations.",
    Icon: GroupIcon,
    StepIcon: GroupsRoundedIcon,
    stepColor: "#0E9046",
    settingsOption: "Team Members",
  },
  {
    key: "workflow",
    question: "How do I set up automation?",
    stepTitle: "Set up automation",
    answer: "Build workflows to automate replies, assignments, and follow-ups.",
    Icon: AccountTreeOutlinedIcon,
    StepIcon: AccountTreeRoundedIcon,
    stepColor: "#8B5CF6",
    workflow: true,
  },
];

function Onboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountid } = useParams();
  const { mode } = useThemeContext();
  const [showWelcome, setShowWelcome] = useState(false);

  const currentWorkspace = useSelector((s) => s.workspace?.currentWorkspace);
  // Shopify is intentionally excluded here — it has no standalone messaging
  // capability (it only sends anything once a WhatsApp number is already
  // connected), so connecting only Shopify must not mark this step complete.
  // whatsappConnected is Meta-only -- a QR-connected workspace is checked separately via
  // whatsappConnectionType (see Dashboard.jsx's own copy of this same comment).
  const isWhatsAppConnectedEitherWay =
    Boolean(currentWorkspace?.whatsappConnected) || currentWorkspace?.whatsappConnectionType === "baileys";
  const isChannelConnected = Boolean(
    isWhatsAppConnectedEitherWay ||
      currentWorkspace?.instagramConnected ||
      currentWorkspace?.facebookConnected ||
      currentWorkspace?.websiteConnected
  );
  // Display-only count across all 5 channels (incl. Shopify) for the step's
  // sub-label — separate from isChannelConnected above, which still gates
  // actual step completion via WA/IG/FB/Website only.
  const connectedChannelCount = [
    isWhatsAppConnectedEitherWay,
    currentWorkspace?.instagramConnected,
    currentWorkspace?.facebookConnected,
    currentWorkspace?.websiteConnected,
    currentWorkspace?.shopifyConnected,
  ].filter(Boolean).length;

  // Account-level progress (0-3), shared across every team member on this
  // account - not per-browser localStorage. `stepCompletion[i]` is true once
  // onboardStep has passed step i+1.
  const [onboardStep, setOnboardStep] = useState(0);

  const resolveToken = async () => {
    let token = getAccessToken();
    if (!token) token = await getAuthToken();
    return token;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await resolveToken();
        if (!token) return;
        const step = await fetchOnboardStep(token);
        if (!cancelled) setOnboardStep(step);
      } catch (err) {
        console.error("Failed to load onboarding progress:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountid]);

  // Advance and persist the account's onboarding step (never regresses - the
  // backend also clamps this with Math.max).
  const advanceOnboardStep = async (step) => {
    setOnboardStep((prev) => Math.max(prev, step));
    try {
      const token = await resolveToken();
      if (!token) return;
      const saved = await pushOnboardStep(step, token);
      setOnboardStep(saved);
    } catch (err) {
      console.error("Failed to save onboarding progress:", err);
    }
  };

  // Step 1 (connect a channel) has a real backend signal - as soon as any
  // channel is connected, push that progress to the account automatically.
  useEffect(() => {
    if (isChannelConnected && onboardStep < 1) {
      advanceOnboardStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChannelConnected, onboardStep]);

  const stepCompletion = onboards.map((_, index) => onboardStep > index);
  const completedCount = stepCompletion.filter(Boolean).length;
  const progressPercent = Math.round((completedCount / onboards.length) * 100);

  useEffect(() => {
    dispatch(setCurrentPage("Onboard"));
  }, [dispatch]);

  // Basic "first visit" check — shows once per account until we wire up a proper
  // backend flag. Revisit once the welcome flow's functionality is finalized.
  useEffect(() => {
    if (!accountid) return;
    const key = `clusterden_onboard_welcome_seen_${accountid}`;
    if (!localStorage.getItem(key)) {
      setShowWelcome(true);
    }
  }, [accountid]);

  useEffect(() => {
    if (!showWelcome) return;
    const colors = ["#0E9046", "#4FC389", "#a0eaa3", "#FFFFFF"];
    const fire = (originX, particleCount, spread) =>
      confetti({
        particleCount,
        spread,
        startVelocity: 55,
        gravity: 0.9,
        ticks: 250,
        origin: { x: originX, y: 0.3 },
        colors,
        zIndex: 1400,
      });
    const timer = setTimeout(() => {
      fire(0.15, 160, 80);
      fire(0.85, 160, 80);
      fire(0.5, 120, 100);
    }, 50);
    const timer2 = setTimeout(() => {
      fire(0.25, 90, 70);
      fire(0.75, 90, 70);
    }, 300);
    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
    };
  }, [showWelcome]);

  const dismissWelcome = () => {
    setShowWelcome(false);
    if (accountid) {
      localStorage.setItem(`clusterden_onboard_welcome_seen_${accountid}`, "1");
    }
  };

  const handleBookSession = () => {
    window.open(WHATSAPP_URL, "_blank", "noopener,noreferrer");
  };

  const handleOnboardNavigate = (onboard) => {
    // Channel step completion is derived from the real connection state (see
    // the effect above) - team/automation just need a visit to their page.
    if (onboard.key !== "channel") {
      const stepNumber = onboards.findIndex((o) => o.key === onboard.key) + 1;
      advanceOnboardStep(stepNumber);
    }

    if (onboard.workflow) {
      navigate(`/home/${accountid}/workflow`);
    } else {
      dispatch(setSelectedOption(onboard.settingsOption));
      navigate(`/home/${accountid}/settings`);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        // Explicit calc height on xs/sm instead of trusting height:"100%" through
        // the nested flex/Grid chain — this app hides TopNav for this route and
        // the percentage chain wasn't reliably leaving the auto-overflow child
        // with less-than-content height on mobile, so overflow:auto never
        // triggered (page looked "dead", no scroll at all). CSS vars are the
        // same ones Home.jsx already keeps in sync (trial banner) or hardcodes
        // (bottom nav height).
        height: {
          xs: "calc(100dvh - var(--trial-banner-height, 0px) - var(--mobile-bottom-nav-height, 56px))",
          sm: "calc(100dvh - var(--trial-banner-height, 0px) - var(--mobile-bottom-nav-height, 62px))",
          md: "100%",
          lg: "100%",
          xl: "100%",
        },
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        margin: 0,
        padding: { xs: 1.5, sm: 2, md: 2.5, lg: 3, xl: 3.5 },
        boxSizing: "border-box",
        backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#161616" : theme.palette.background.default),
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Dialog
        open={showWelcome}
        onClose={dismissWelcome}
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: 4,
            width: "100%",
            maxWidth: 1040,
            maxHeight: { xs: "94vh", md: "90vh" },
            overflowY: "auto",
            overflowX: "hidden",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
          {/* Left panel — brand intro + illustration (placeholder until Rahim supplies the real image) */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              flex: "0 0 38%",
              px: 4.5,
              py: 4.5,
              [ONBOARD_COMPACT_HEIGHT_MQ]: { px: 3, py: 3 },
              background: (theme) =>
                theme.palette.mode === "dark"
                  ? "linear-gradient(160deg, rgba(14,144,70,0.28) 0%, rgba(14,144,70,0.08) 100%)"
                  : "linear-gradient(160deg, rgba(14,144,70,0.16) 0%, rgba(79,195,137,0.05) 100%)",
            }}
          >
            <Box
              component="img"
              src={mode === "dark" ? ClusterDenLogoWhite : ClusterDenLogo}
              alt="Clusterden"
              sx={{ alignSelf: "flex-start", height: 40, width: "auto", maxWidth: 210, objectFit: "contain", mb: 4, [ONBOARD_COMPACT_HEIGHT_MQ]: { mb: 2.5 } }}
            />

            <Typography
              sx={{
                fontFamily: "Instrument Sans, sans-serif",
                fontWeight: 800,
                fontSize: "2.35rem",
                lineHeight: 1.15,
                color: (theme) => (theme.palette.mode === "dark" ? "#f5f5f5" : "#121a34"),
                [ONBOARD_COMPACT_HEIGHT_MQ]: { fontSize: "1.7rem" },
              }}
            >
              Welcome to
            </Typography>
            <Typography
              sx={{
                fontFamily: "Instrument Sans, sans-serif",
                fontWeight: 800,
                fontSize: "2.35rem",
                lineHeight: 1.15,
                color: "primary.main",
                mb: 2,
                [ONBOARD_COMPACT_HEIGHT_MQ]: { fontSize: "1.7rem", mb: 1.25 },
              }}
            >
              Clusterden
            </Typography>
            <Typography
              sx={{
                fontFamily: "Instrument Sans, sans-serif",
                fontSize: "1.1rem",
                lineHeight: 1.6,
                color: (theme) => (theme.palette.mode === "dark" ? "#c7cbd1" : "#4B5563"),
                mb: 4,
                [ONBOARD_COMPACT_HEIGHT_MQ]: { mb: 2 },
              }}
            >
              Let's set up your workspace in a few simple steps.
            </Typography>

            <Box
              sx={{
                flex: 1,
                minHeight: 200,
                [ONBOARD_COMPACT_HEIGHT_MQ]: { minHeight: 100 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={WelcomeIllustration}
                alt=""
                sx={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>
          </Box>

          {/* Right panel — steps */}
          <Box
            sx={{
              position: "relative",
              flex: 1,
              px: { xs: 3.5, md: 5.5 },
              py: { xs: 4.5, md: 5.5 },
              [ONBOARD_COMPACT_HEIGHT_MQ]: { px: { xs: 2.5, md: 3.5 }, py: { xs: 2.5, md: 3 } },
              backgroundColor: "background.paper",
            }}
          >
            <IconButton
              onClick={dismissWelcome}
              sx={{ position: "absolute", top: 14, right: 14, color: "text.secondary" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            <Typography sx={{ fontWeight: 700, fontSize: "1.65rem", mb: 4, color: "text.primary", [ONBOARD_COMPACT_HEIGHT_MQ]: { mb: 2 } }}>
              Set up your workspace in <Box component="span" sx={{ color: "primary.main" }}>3 easy steps</Box>
            </Typography>

            <Box sx={{ position: "relative", display: "flex", flexDirection: "column", mb: 4, [ONBOARD_COMPACT_HEIGHT_MQ]: { mb: 2 } }}>
              {/* Single continuous connector behind the numbered circles — offsets assume
                  each step card below shares the same fixed minHeight (100px), so all three
                  circles land at identical y-intervals and a straight line threads through them. */}
              <Box
                sx={{
                  position: "absolute",
                  left: 19,
                  top: 50,
                  bottom: 50,
                  borderLeft: "2.5px dashed",
                  borderColor: (theme) =>
                    theme.palette.mode === "dark" ? "rgba(79,195,137,0.4)" : "rgba(14,144,70,0.3)",
                  zIndex: 0,
                }}
              />
              {onboards.map((onboard, index) => (
                <Box
                  key={onboard.question}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2.5,
                    position: "relative",
                    zIndex: 1,
                    mb: index < onboards.length - 1 ? 3 : 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      flexShrink: 0,
                      flexGrow: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        minWidth: 38,
                        minHeight: 38,
                        maxWidth: 38,
                        maxHeight: 38,
                        flexShrink: 0,
                        flexGrow: 0,
                        boxSizing: "border-box",
                        borderRadius: "50%",
                        backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#2A2A2A" : "#E9EAEC"),
                        color: (theme) => (theme.palette.mode === "dark" ? "#E5E7EB" : "#374151"),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "15px",
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      {index + 1}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 100,
                      boxSizing: "border-box",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "14px",
                      p: 2.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: "14px",
                        backgroundColor: (theme) => `${onboard.stepColor}${theme.palette.mode === "dark" ? "33" : "1A"}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <onboard.StepIcon sx={{ fontSize: 26, color: onboard.stepColor }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", mb: 0.5, color: "text.primary" }}>{onboard.stepTitle}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.9rem", lineHeight: 1.5 }}>
                        {onboard.answer}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>

            <Button
              fullWidth
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={dismissWelcome}
              sx={{
                borderRadius: "12px",
                py: 1.75,
                [ONBOARD_COMPACT_HEIGHT_MQ]: { py: 1.1 },
                fontWeight: 600,
                fontSize: "1rem",
                textTransform: "none",
                fontFamily: "Instrument Sans, sans-serif",
                backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#4FC389" : "#44B57C"),
                color: "#ffffff",
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "none"
                    : "0 2px 8px rgba(68,181,124,0.25)",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#3aad77" : "#38a06c"),
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "none"
                      : "0 8px 20px rgba(68,181,124,0.35)",
                },
              }}
            >
              Let's get started
            </Button>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75, mt: 2, [ONBOARD_COMPACT_HEIGHT_MQ]: { mt: 1 } }}>
              <ShieldOutlinedIcon sx={{ fontSize: 15, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary">
                You can always change these settings later.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Dialog>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: { xs: 1.75, sm: 2.5, md: 3 },
          [ONBOARD_COMPACT_HEIGHT_MQ]: { gap: 1.75 },
          maxWidth: "1000px",
          width: "100%",
          mx: "auto",
          boxSizing: "border-box",
        }}
      >
        {/* Hero banner — welcome + book-a-session */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 3,
            borderRadius: { xs: "14px", sm: "20px" },
            overflow: "hidden",
            boxSizing: "border-box",
            px: { xs: 2, sm: 3, md: 5 },
            py: { xs: 2, sm: 3, md: 3.5 },
            [ONBOARD_COMPACT_HEIGHT_MQ]: { py: { xs: 2, md: 2 } },
            background: (theme) =>
              theme.palette.mode === "dark"
                ? "linear-gradient(120deg, rgba(14,144,70,0.22) 0%, rgba(14,144,70,0.06) 100%)"
                : "linear-gradient(120deg, rgba(14,144,70,0.1) 0%, rgba(79,195,137,0.03) 100%)",
          }}
        >
          <AutoAwesomeIcon sx={{ position: "absolute", top: 28, right: "38%", fontSize: 16, color: "primary.main", opacity: 0.35, display: { xs: "none", md: "block" } }} />
          <AutoAwesomeIcon sx={{ position: "absolute", bottom: 36, right: "8%", fontSize: 20, color: "primary.main", opacity: 0.25, display: { xs: "none", md: "block" } }} />

          <Box sx={{ position: "relative", zIndex: 1, maxWidth: 480 }}>

            <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.85rem", sm: "2.1rem" }, lineHeight: 1.25, color: "text.primary", [ONBOARD_COMPACT_HEIGHT_MQ]: { fontSize: { xs: "1.5rem", md: "1.7rem" } } }}>
              Let's set up your workspace{" "}
              <Box component="span" sx={{ color: "primary.main" }}>
                and get you started!
              </Box>
            </Typography>

            <Typography color="text.secondary" sx={{ fontSize: "0.95rem", mt: { xs: 1, md: 1.75 }, mb: { xs: 1.75, md: 3.5 }, lineHeight: 1.55, [ONBOARD_COMPACT_HEIGHT_MQ]: { mt: 1, mb: 1.75 } }}>
              Follow this quick 3-step guide to connect your channels, invite your team, and automate like a pro.
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.25, sm: 2.5 }, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                startIcon={<WhatsAppIcon />}
                onClick={handleBookSession}
                sx={{ whiteSpace: "nowrap", borderRadius: "10px", px: { xs: 1.75, sm: 2.5 }, py: { xs: 0.75, sm: 1.1 }, fontSize: "0.95rem" }}
              >
                Book a session
              </Button>
              <Box sx={{ width: "1px", alignSelf: "stretch", backgroundColor: "divider", display: { xs: "none", sm: "block" } }} />
              <Typography color="text.secondary" sx={{ fontSize: "0.95rem", maxWidth: { xs: "100%", sm: 240 }, lineHeight: 1.5 }}>
                Get a 1-on-1 walkthrough on WhatsApp with our team.
              </Typography>
            </Box>
          </Box>

          <Box
            component="img"
            src={WelcomeIllustration}
            alt=""
            sx={{
              display: { xs: "none", md: "block" },
              position: "relative",
              zIndex: 1,
              maxWidth: "48%",
              maxHeight: 260,
              [ONBOARD_COMPACT_HEIGHT_MQ]: { maxHeight: 150 },
              width: "auto",
              height: "auto",
              objectFit: "contain",
            }}
          />
        </Box>

        {/* Your progress — checklist card. Locking/completion is visual-only for now (Rahim: functionality later). */}
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: { xs: "12px", sm: "16px" },
            backgroundColor: "background.paper",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              gap: { xs: 0.75, sm: 2 },
              px: { xs: 2, sm: 3 },
              py: { xs: 1.5, sm: 2.5 },
              [ONBOARD_COMPACT_HEIGHT_MQ]: { py: 1.75 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "1.2rem", color: "text.primary" }}>Your progress</Typography>
              <Typography color="text.secondary" sx={{ fontSize: "0.95rem", display: { xs: "none", sm: "block" } }}>
                {completedCount} / {onboards.length} completed
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 } }}>
              <Typography color="text.secondary" sx={{ fontSize: "0.95rem", display: { xs: "block", sm: "none" }, whiteSpace: "nowrap" }}>
                {completedCount} / {onboards.length}
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  height: 6,
                  borderRadius: 999,
                  backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"),
                  overflow: "hidden",
                }}
              >
                <Box sx={{ width: `${progressPercent}%`, height: "100%", backgroundColor: "primary.main", borderRadius: 999, transition: "width 0.3s ease" }} />
              </Box>
              <Typography color="text.secondary" sx={{ fontSize: "0.95rem" }}>
                {progressPercent}%
              </Typography>
            </Box>
          </Box>

          {onboards.map((onboard, index) => {
            const isCompleted = stepCompletion[index];
            const isActive = index === 0 || stepCompletion[index - 1];
            return (
              <Box
                key={onboard.question}
                onClick={isActive ? () => handleOnboardNavigate(onboard) : undefined}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1.25, sm: 2 },
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1.5, sm: 2.75 },
                  [ONBOARD_COMPACT_HEIGHT_MQ]: { py: 1.75 },
                  boxSizing: "border-box",
                  cursor: isActive ? "pointer" : "default",
                  borderTop: "1px solid",
                  borderColor: "divider",
                  transition: "background-color 0.15s ease, box-shadow 0.15s ease",
                  ...(isActive && {
                    "&:hover": {
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "rgba(79,195,137,0.08)" : "rgba(14,144,70,0.05)",
                      boxShadow: (theme) =>
                        `inset 3px 0 0 ${theme.palette.mode === "dark" ? "#4FC389" : "#0E9046"}`,
                      "& .step-number-circle": {
                        backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#333333" : "#E4E6E9"),
                      },
                      "& .step-icon-badge": {
                        backgroundColor: (theme) => `${onboard.stepColor}${theme.palette.mode === "dark" ? "40" : "26"}`,
                      },
                      "& .step-icon-badge svg": {
                        color: `${onboard.stepColor} !important`,
                      },
                    },
                  }),
                }}
              >
                <Box
                  className="step-number-circle"
                  sx={{
                    position: "relative",
                    width: { xs: 28, sm: 36 },
                    height: { xs: 28, sm: 36 },
                    borderRadius: "50%",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: { xs: "12px", sm: "14px" },
                    transition: "background-color 0.15s ease, color 0.15s ease",
                    backgroundColor: (theme) =>
                      isCompleted
                        ? theme.palette.mode === "dark"
                          ? "rgba(79,195,137,0.25)"
                          : "rgba(14,144,70,0.12)"
                        : theme.palette.mode === "dark"
                        ? "#2A2A2A"
                        : "#EEF0F2",
                    color: isActive ? "text.primary" : "text.disabled",
                  }}
                >
                  {isCompleted ? (
                    <CheckCircleRoundedIcon sx={{ fontSize: { xs: 16, sm: 20 }, color: (theme) => (theme.palette.mode === "dark" ? "#4FC389" : "#0E9046") }} />
                  ) : isActive ? (
                    index + 1
                  ) : (
                    <LockOutlinedIcon sx={{ fontSize: { xs: 13, sm: 16 } }} />
                  )}
                </Box>

                <Box
                  className="step-icon-badge"
                  sx={{
                    width: { xs: 30, sm: 40 },
                    height: { xs: 30, sm: 40 },
                    borderRadius: { xs: "8px", sm: "10px" },
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background-color 0.15s ease",
                    backgroundColor: (theme) =>
                      isActive
                        ? `${onboard.stepColor}${theme.palette.mode === "dark" ? "33" : "1A"}`
                        : theme.palette.mode === "dark"
                        ? "#232323"
                        : "#F3F4F6",
                  }}
                >
                  <onboard.StepIcon fontSize="small" sx={{ color: isActive ? onboard.stepColor : "text.disabled", transition: "color 0.15s ease", fontSize: { xs: 16, sm: 20 } }} />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: "1.05rem", color: isActive ? "text.primary" : "text.secondary" }}>
                    {onboard.stepTitle}
                    {onboard.key === "channel" && (
                      <Box
                        component="span"
                        sx={{ ml: 1, fontWeight: 500, fontSize: "0.8rem", color: "text.secondary" }}
                      >
                        ({connectedChannelCount}/5 connected)
                      </Box>
                    )}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: "0.92rem", mt: 0.25, lineHeight: 1.4 }}>
                    {onboard.key === "channel"
                      ? "Connect WhatsApp, Facebook, Instagram, your Website, or Shopify to start receiving and sending messages from your inbox."
                      : onboard.answer}
                  </Typography>
                </Box>

                {isActive && <ChevronRightIcon sx={{ color: "text.secondary", flexShrink: 0, fontSize: { xs: 18, sm: 24 } }} />}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

export default Onboard;
